import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LenisProvider from "./components/LenisProvider";
import CustomCursor from "./components/CustomCursor";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import ScrollRails from "./components/ScrollRails";
import LogoLoader from "./components/LogoLoader";
import StructuredData, { organizationLD, websiteLD } from "./components/StructuredData";

import Home from "./pages/Home";
import Work from "./pages/Work";
import CaseStudy from "./pages/CaseStudy";
import CaseStudies from "./pages/CaseStudies";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Marketing from "./pages/Marketing";
import BlogArticle, { BlogIndex } from "./pages/Blog";
import AI from "./pages/AI";
import Album from "./pages/Album";

import AdminLogin from "./pages/admin/Login";
import AdminLayout from "./components/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import WorksAdmin from "./pages/admin/WorksAdmin";
import Inquiries from "./pages/admin/Inquiries";
import SettingsAdmin from "./pages/admin/Settings";
import AboutAdmin from "./pages/admin/AboutAdmin";
import ContactAdmin from "./pages/admin/ContactAdmin";
import Account from "./pages/admin/Account";
import MarketingAdmin from "./pages/admin/MarketingAdmin";
import TypographyAdmin from "./pages/admin/TypographyAdmin";
import BlogAdmin from "./pages/admin/BlogAdmin";
import AlbumAdmin from "./pages/admin/AlbumAdmin";

import { api } from "./lib/api";

function PublicLayout({ children, settings }) {
  return (
    <LenisProvider>
      <div className="ap-grain">
        {/* Global JSON-LD — Organization + WebSite. Injected once for every
            public page so ChatGPT / Perplexity / Google AI can cite the studio. */}
        <StructuredData id="organization" data={organizationLD} />
        <StructuredData id="website" data={websiteLD} />
        <LogoLoader minMs={1800} />
        <CustomCursor />
        <ScrollRails />
        <Navbar settings={settings} />
        <AnimatedOutlet>{children}</AnimatedOutlet>
        <Footer settings={settings} />
        <WhatsAppButton settings={settings} />
      </div>
    </LenisProvider>
  );
}

function AnimatedOutlet({ children }) {
  const location = useLocation();
  // Fire a PostHog $pageview on every SPA route change so funnels see accurate
  // path progression (default posthog.init only captures the initial load).
  useEffect(() => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture("$pageview", {
          path: location.pathname,
          search: location.search,
        });
      }
    } catch {}
  }, [location.pathname, location.search]);
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data || {}));
  }, []);

  useEffect(() => {
    // Only set the global default title if no per-page <PageSEO /> has claimed it.
    // PageSEO sets a marker on <html data-page-seo="1"> when active.
    if (settings?.agency_name && !document.documentElement.dataset.pageSeo) {
      document.title = `${settings.agency_name} — ${settings.tagline || "Creative studio"}`;
    }
  }, [settings]);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public site */}
          <Route
            path="/"
            element={
              <PublicLayout settings={settings}>
                <Home settings={settings} />
              </PublicLayout>
            }
          />
          <Route
            path="/work"
            element={
              <PublicLayout settings={settings}>
                <Work settings={settings} />
              </PublicLayout>
            }
          />
          <Route
            path="/work/:slug"
            element={
              <PublicLayout settings={settings}>
                <CaseStudy settings={settings} />
              </PublicLayout>
            }
          />
          <Route
            path="/work/:slug/case-study"
            element={
              <PublicLayout settings={settings}>
                <CaseStudy settings={settings} detailed />
              </PublicLayout>
            }
          />
          <Route
            path="/case-studies"
            element={
              <PublicLayout settings={settings}>
                <CaseStudies />
              </PublicLayout>
            }
          />
          <Route
            path="/about"
            element={
              <PublicLayout settings={settings}>
                <About settings={settings} />
              </PublicLayout>
            }
          />
          <Route
            path="/contact"
            element={
              <PublicLayout settings={settings}>
                <Contact settings={settings} />
              </PublicLayout>
            }
          />
          <Route
            path="/marketing"
            element={
              <PublicLayout settings={settings}>
                <Marketing settings={settings} />
              </PublicLayout>
            }
          />
          <Route
            path="/blog"
            element={
              <PublicLayout settings={settings}>
                <BlogIndex settings={settings} />
              </PublicLayout>
            }
          />
          <Route
            path="/blog/:slug"
            element={
              <PublicLayout settings={settings}>
                <BlogArticle settings={settings} />
              </PublicLayout>
            }
          />
          <Route
            path="/album"
            element={
              <PublicLayout settings={settings}>
                <Album settings={settings} />
              </PublicLayout>
            }
          />
          {settings?.show_ai_tab && (
            <Route
              path="/ai"
              element={
                <PublicLayout settings={settings}>
                  <AI settings={settings} />
                </PublicLayout>
              }
            />
          )}

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="works" element={<WorksAdmin />} />
            <Route path="inquiries" element={<Inquiries />} />
            <Route path="settings" element={<SettingsAdmin />} />
            <Route path="about" element={<AboutAdmin />} />
            <Route path="contact" element={<ContactAdmin />} />
            <Route path="marketing" element={<MarketingAdmin />} />
            <Route path="typography" element={<TypographyAdmin />} />
            <Route path="blog" element={<BlogAdmin />} />
            <Route path="blog/:slug" element={<BlogAdmin />} />
            <Route path="album" element={<AlbumAdmin />} />
            <Route path="account" element={<Account />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
